class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => this.classList.add('animate', 'active'));

    this.addEventListener(
      'transitionend',
      () => {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusEl = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (container) trapFocus(container, focusEl);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
  }

  close() {
    this.classList.remove('animate', 'active');
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    try { removeTrapFocus(this.activeElement); } catch (_) {}
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner?.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!el) return;

      const html = parsedState.sections?.[section.id];
      if (!html) return;

      el.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    setTimeout(() => {
      // Re-attach overlay listener after content replacement
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));

      // Sync header cart bubble from the freshly rendered drawer count
      const drawerCount = this.querySelector('.drawer__count');
      const headerBubble = document.querySelector('#cart-icon-bubble .header__cart-count');
      if (drawerCount && headerBubble) {
        headerBubble.textContent = drawerCount.textContent;
      }

      // Only (re-)open + re-trap focus when the drawer isn't already open.
      // When re-rendering an already-open drawer (e.g. applying a discount
      // code from inside it), calling open() registers a one-time
      // `transitionend` -> trapFocus listener that later fires on an unrelated
      // transition (entries count-up, boost glow) and steals focus from the
      // code input the shopper just clicked.
      if (!this.classList.contains('active')) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// ---------------------------------------------------------------------------

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this._pendingQty = {};
    this._debounceTimers = {};

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="plus"], button[name="minus"]');
      if (!btn) return;

      const qtyWrapper = btn.closest('quantity-input');
      if (!qtyWrapper) return;
      const input = qtyWrapper.querySelector('.quantity__input');
      if (!input) return;

      const line = input.dataset.index;
      const unitPrice = this._getUnitPrice(line);
      const delta = btn.name === 'plus' ? 1 : -1;

      if (this._pendingQty[line] === undefined) {
        this._pendingQty[line] = parseInt(input.getAttribute('value'), 10) || parseInt(input.value, 10);
      }

      const proposed = this._pendingQty[line] + delta;
      const maxQty = input.max ? parseInt(input.max, 10) : null;

      if (proposed < 1) return;
      if (maxQty && proposed > maxQty) {
        this._showError(line, 'Maximum quantity is ' + maxQty);
        return;
      }

      this._pendingQty[line] = proposed;

      // Optimistic UI
      if (unitPrice && proposed > 0) {
        this._setLinePrice(line, unitPrice * proposed);
        const newTotal = this._calcTotal();
        this._setTotal(newTotal);
        this._updateEntries();
      }

      clearTimeout(this._debounceTimers[line]);
      this._debounceTimers[line] = setTimeout(() => {
        const qty = this._pendingQty[line];
        delete this._pendingQty[line];
        if (line) this.updateQuantity(line, qty);
      }, 400);
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  updateQuantity(line, quantity) {
    const lineNum = parseInt(line, 10);

    // Optimistic fade for removal
    if (quantity === 0) {
      const row = document.getElementById('CartDrawer-Item-' + lineNum);
      if (row) { row.style.transition = 'opacity 0.2s'; row.style.opacity = '0.4'; }
    }

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: lineNum, quantity }),
    })
      .then((r) => r.json())
      .then((cart) => {
        // Server returned an error
        if (cart.status === 422 || !cart.items) {
          const msg = cart.description || cart.message || (window.shftdLocale && window.shftdLocale.qty_error) || 'Could not update quantity.';
          this._showError(line, msg);
          this._resetRowOpacity(lineNum);
          // Revert optimistic price
          fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((realCart) => {
              const realItem = realCart.items[lineNum - 1];
              if (realItem) {
                const qtyInput = document.getElementById('Drawer-quantity-' + line);
                if (qtyInput) {
                  qtyInput.value = realItem.quantity;
                  qtyInput.setAttribute('value', realItem.quantity);
                }
                delete this._pendingQty[line];
                this._setLinePrice(line, realItem.final_line_price);
              }
              this._syncAll(realCart.total_price, realCart.item_count);
            });
          return;
        }

        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) drawerEl.classList.toggle('is-empty', cart.item_count === 0);

        // Item removed or no longer exists — re-render the drawer section
        if (quantity === 0 || !cart.items[lineNum - 1]) {
          this._rerenderDrawer(cart);
          return;
        }

        // Quantity updated in place
        const item = cart.items[lineNum - 1];
        this._setLinePrice(line, item.final_line_price);

        if (this._pendingQty[line] === undefined) {
          const qtyInput = document.getElementById('Drawer-quantity-' + line);
          if (qtyInput) {
            qtyInput.value = item.quantity;
            qtyInput.setAttribute('value', item.quantity);
          }
        }

        this._syncAll(cart.total_price, cart.item_count);
      })
      .catch(() => {
        this._resetRowOpacity(lineNum);
        this._showError(line, 'Something went wrong. Please try again.');
      });
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  _rerenderDrawer(cart) {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newCartDrawer = doc.querySelector('#CartDrawer');
        const currentCartDrawer = document.getElementById('CartDrawer');

        if (newCartDrawer && currentCartDrawer) {
          currentCartDrawer.innerHTML = newCartDrawer.innerHTML;

          // Re-attach overlay close
          const drawerEl = document.querySelector('cart-drawer');
          const overlay = document.getElementById('CartDrawer-Overlay');
          if (overlay && drawerEl) {
            overlay.addEventListener('click', drawerEl.close.bind(drawerEl));
          }
        }

        this._syncAll(cart.total_price, cart.item_count);
      })
      .catch(() => location.reload()); // last-resort fallback only
  }

  _resetRowOpacity(lineNum) {
    const row = document.getElementById('CartDrawer-Item-' + lineNum);
    if (row) row.style.opacity = '';
  }

  _calcTotal() {
    let total = 0;
    this.querySelectorAll('.cart-item').forEach((row) => {
      const line = row.id.replace('CartDrawer-Item-', '');
      const up = parseInt(row.getAttribute('data-unit-price'), 10) || 0;
      let qty = this._pendingQty[line];
      if (qty === undefined) {
        const input = row.querySelector('.quantity__input');
        qty = input ? parseInt(input.value, 10) : 0;
      }
      total += up * qty;
    });
    return total;
  }

  _getUnitPrice(line) {
    const row = document.getElementById('CartDrawer-Item-' + line);
    if (!row) return 0;
    return parseInt(row.getAttribute('data-unit-price'), 10) || 0;
  }

  _setLinePrice(line, cents) {
    const el = document.querySelector('#CartDrawer-Item-' + line + ' .cart-item__price-wrapper');
    if (el) el.innerHTML = '<span class="price price--end">' + _fmt(cents) + '</span>';
  }

  _setTotal(cents) {
    const tv = document.querySelector('.cart-drawer .totals__total-value');
    if (tv) tv.textContent = _fmt(cents) + ' CAD';
  }

  _updateEntries() {
    // No-op: the entries banner is owned by cart-entries-preview.js, which paints
    // the BOOSTED total optimistically on a qty change and reconciles with the
    // backend. Writing the un-boosted BASE here made the total flash base and then
    // re-animate to boosted a second later (the lag). Left as a no-op so the
    // public alias + existing call sites keep working.
  }

  _showError(line, message) {
    const row = document.getElementById('CartDrawer-Item-' + line);
    if (!row) return;
    const existing = row.querySelector('.cart-drawer__line-error');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.className = 'cart-drawer__line-error';
    el.textContent = message;
    row.appendChild(el);
    setTimeout(() => el.remove(), 4000);
  }

  _syncAll(totalCents, itemCount) {
    this._setTotal(totalCents);
    this._updateEntries();
    const bubble = document.querySelector('#cart-icon-bubble .header__cart-count');
    if (bubble) bubble.textContent = itemCount;
    const hc = document.querySelector('.drawer__count');
    if (hc) hc.textContent = itemCount;
  }

  // Keep old public names as aliases so any external callers don't break
  syncAll(totalCents, itemCount) { this._syncAll(totalCents, itemCount); }
  showError(line, message)       { this._showError(line, message); }
  calcTotal()                    { return this._calcTotal(); }
  getUnitPrice(line)             { return this._getUnitPrice(line); }
  setLinePrice(line, cents)      { this._setLinePrice(line, cents); }
  setTotal(cents)                { this._setTotal(cents); }
  updateEntries()                { this._updateEntries(); }
  readTotal() {
    const tv = document.querySelector('.cart-drawer .totals__total-value');
    if (!tv) return 0;
    const match = tv.textContent.replace(/[^0-9.]/g, '');
    return match ? Math.round(parseFloat(match) * 100) : 0;
  }

  getSectionsToRender() {
    return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' }];
  }

  // Block the parent's debounced onChange — drawer uses its own click handler
  onChange() {}

  // Disable Dawn's loading spinners — they blank out the price display
  enableLoading()  {}
  disableLoading() {}
}

function _fmt(cents) {
  return '$' + (cents / 100).toFixed(2);
}

customElements.define('cart-drawer-items', CartDrawerItems);

// ---------------------------------------------------------------------------
// SHFTD drawer enhancements: free-shipping near-miss nudge + pre-checkout
// intercept, upsell quick-add, and the entries bonus bridge. Every listener
// is delegated on document so it survives #CartDrawer re-renders, and the
// anchor elements are Liquid-rendered inside #CartDrawer so re-renders bring
// them back on their own.
// ---------------------------------------------------------------------------

(function () {
  'use strict';

  // Owner rule: the intercept only fires for carts of $85+ (gap <= $35).
  var NEAR_MISS_MAX_CENTS = 3500;
  var INTERCEPT_KEY = 'shftd_ship_intercept';
  // In-memory fallback so the intercept still fires at most once even when
  // sessionStorage is unavailable (private mode / storage disabled).
  var interceptShownThisPage = false;

  // Pre-discount subtotal from the drawer rows (original unit price × the
  // live qty input): the same basis the tier ladder qualifies the $-rungs
  // on, so the nudge and the ladder's free-ship rung always agree.
  function subtotalCents() {
    var sum = 0;
    document.querySelectorAll('#CartDrawer .cart-item').forEach(function (row) {
      var unit = parseInt(row.getAttribute('data-original-price'), 10) ||
        parseInt(row.getAttribute('data-unit-price'), 10) || 0;
      var input = row.querySelector('.quantity__input');
      var qty = input ? parseInt(input.value, 10) || 0 : 0;
      sum += unit * qty;
    });
    return sum;
  }

  // Cents left to free shipping, or null when it doesn't apply. Digital-only
  // carts render no [data-ship-zone] (Liquid gates on cart.requires_shipping),
  // so the attribute's absence is the gate.
  function shipGapCents() {
    var el = document.querySelector('[data-ship-zone]');
    if (!el) return null;
    var threshold = parseInt(el.getAttribute('data-threshold'), 10) || 0;
    if (threshold <= 0) return null;
    return threshold - subtotalCents();
  }

  function isNearMiss(gap) {
    return gap != null && gap > 0 && gap <= NEAR_MISS_MAX_CENTS;
  }

  function paintGap(gap) {
    document.querySelectorAll('[data-ship-gap]').forEach(function (el) {
      el.textContent = _fmt(gap);
    });
  }

  // ---- Pre-checkout intercept ----

  function interceptSeen() {
    if (interceptShownThisPage) return true;
    try { return !!sessionStorage.getItem(INTERCEPT_KEY); } catch (_) { return false; }
  }

  function markInterceptSeen() {
    interceptShownThisPage = true;
    try { sessionStorage.setItem(INTERCEPT_KEY, '1'); } catch (_) {}
  }

  // The intercept is a full-drawer takeover: the whole panel morphs into a
  // dark staged statement with one-tap gap-closer offers (CSS reveals each
  // line in sequence off .is-open). Plain timeouts, not rAF: rAF stalls in
  // backgrounded tabs.
  var SWITCH_MS = 240;

  // Adds made from inside the takeover are applied to the drawer DOM only
  // on exit: the takeover node itself lives inside #CartDrawer, so an
  // immediate renderContents would replace it mid-view. Until then the gap
  // math runs on this accumulator.
  var pendingSections = null;
  var pendingAddedCents = 0;

  function takeoverEl() {
    return document.querySelector('[data-ship-intercept]');
  }

  function switchMs() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : SWITCH_MS;
    } catch (_) { return SWITCH_MS; }
  }

  function effectiveGap() {
    var gap = shipGapCents();
    return gap == null ? null : gap - pendingAddedCents;
  }

  // Rank the candidate pool against the live gap and reveal the best two.
  // Owner rule: the one-tap total must AT LEAST match the gap; the sweet
  // spot is up to $5 over (take the AOV push, highest-in-band first); more
  // than $15 over the gap is benched entirely (no $85 hoodie for a $15
  // gap). Each button adds EXACTLY the computed units in one tap.
  var SWEET_OVER_CENTS = 500;
  var MAX_OVER_CENTS = 1500;
  var MAX_UNITS = 5;
  var MAX_OFFERS = 2; // physical cards; the digital flex pass takes the hero slot

  // Tier math mirroring cart-tiers-bar.js (and the PDP pack chips): additive
  // multiplier rungs raise the per-$ rate, bonus rungs add flat entries.
  function tierAddsAt(subCents) {
    var base = parseFloat(window.SHFTD_DEFAULT_MULTIPLIER) || 1;
    var add = 0;
    var cfg = window.SHFTD_CART_TIERS;
    if (cfg && cfg.enabled && Array.isArray(cfg.tiers)) {
      cfg.tiers.forEach(function (t) {
        var at = Math.round(parseFloat(t.minSubtotalCents) || 0);
        if (at <= 0 || at > subCents) return;
        (Array.isArray(t.effects) ? t.effects : []).forEach(function (e) {
          var v = parseFloat(e.value) || 0;
          if (e.type === 'entry_multiplier') add += v;
          // pct rungs are stored as FRACTIONS (0.25 = +25% of base), matching
          // cart-tiers-bar.js resolvedAdd: base * cumPct.
          else if (e.type === 'entry_multiplier_pct') add += base * v;
        });
      });
    }
    return add;
  }

  // The award runs on an "ends in 9" effective unit price: a unit whose
  // whole-dollar floor ends in 9 counts as the next dollar, so $19 and $19.99
  // both award as $20. Mirrored from shftd-backend entries.ts
  // endsInNineEffectivePrice so the drawer never under-promises entries.
  function _rate(v) {
    return v % 1 === 0 ? String(v) : String(Math.round(v * 10) / 10);
  }

  // Entries read as text under the name; the rate rides the thumbnail corner.
  function paintOffer(offer, entries, rate) {
    if (!offer) return;
    var e = offer.querySelector('[data-offer-entries]');
    if (e) {
      // Rate belongs to the entries, so it is said as part of that sentence
      // rather than floating as a second bare number on the card.
      e.innerHTML = '+' + entries.toLocaleString() + ' entries' +
        '<small class="drawer__takeover-offer-rate">at ' + _rate(rate) + 'X</small>';
    }
  }

  function effectiveDollars(cents) {
    var d = (cents || 0) / 100;
    var f = Math.floor(d);
    return f % 10 === 9 ? f + 1 : d;
  }

  function flatBonusesBetween(fromCents, toCents) {
    var base = parseFloat(window.SHFTD_DEFAULT_MULTIPLIER) || 1;
    var flat = 0;
    var cfg = window.SHFTD_CART_TIERS;
    if (cfg && cfg.enabled && Array.isArray(cfg.tiers)) {
      cfg.tiers.forEach(function (t) {
        var at = Math.round(parseFloat(t.minSubtotalCents) || 0);
        if (at <= fromCents || at > toCents) return;
        (Array.isArray(t.effects) ? t.effects : []).forEach(function (e) {
          var v = parseFloat(e.value) || 0;
          if (e.type === 'bonus_entries') flat += v;
          else if (e.type === 'bonus_entries_base') { var bb = base * v; flat += bb > 0 ? Math.ceil(bb / 5) * 5 : 0; }
        });
      });
    }
    return Math.round(flat);
  }

  // Session-shaped digital closer: pick the flex-pass variant priced
  // closest to gap + $5 (always clearing the threshold with margin), label
  // it for THIS cart, and show its multiplier + instant entries. Owner
  // intent: highest-margin add, sold only here, free shipping covered.
  var FLEX_PUSH_CENTS = 500;

  function shapeFlex(gap, curSub) {
    var flex = document.querySelector('[data-takeover-flex]');
    if (!flex) return;
    flex.hidden = true;
    if (gap == null || gap <= 0) return;
    var variants;
    try { variants = JSON.parse(flex.getAttribute('data-flex-variants') || '[]'); } catch (_) { variants = []; }
    if (!variants.length) return;
    var target = gap + FLEX_PUSH_CENTS;
    var pick = null;
    variants.forEach(function (v) {
      if (v.price >= target && (!pick || v.price < pick.price)) pick = v;
    });
    if (!pick) {
      variants.forEach(function (v) { if (!pick || v.price > pick.price) pick = v; });
    }
    if (!pick || pick.price < gap) return; // cannot even clear the threshold
    // The product's own metafield rate REPLACES the store base for this line,
    // but the cart tier's per-dollar add still lands on these dollars too
    // (backend: tierAddEntries = effectiveSubtotal * add_per_dollar, and the
    // boost's price is part of that subtotal). So the rate the shopper actually
    // earns is metafield + tier add, which also keeps it exactly one above the
    // prevailing gear rate at every subtotal.
    var newSubFlex = curSub + pick.price;
    var mult = (parseFloat(flex.getAttribute('data-flex-mult')) || 1) + tierAddsAt(newSubFlex);
    var entries = Math.round(mult * effectiveDollars(pick.price)) + flatBonusesBetween(curSub, newSubFlex);
    var priceLabel = pick.price % 100 === 0 ? '$' + (pick.price / 100) : _fmt(pick.price);
    var btn = flex.querySelector('[data-flex-btn]');
    if (btn) {
      btn.setAttribute('data-variant', pick.id);
      btn.setAttribute('data-unit-price', pick.price);
      btn.setAttribute('data-qty', 1);
      btn.innerHTML = '<span class="drawer__takeover-flex-main">' +
        '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z"/></svg>' +
        'LOCK IT IN \u00b7 ' + priceLabel + '</span>' +
        '<small>INSTEAD OF PAYING FOR SHIPPING</small>';
      btn.setAttribute('aria-label', 'Lock it in: adds the ' + _fmt(pick.price) + ' entry ticket to your cart, ' + entries + ' entries, clears free shipping');
      btn.disabled = false;
    }
    // Scene texts: the spotlight number (and its reflection), ticket stub, price chip.
    var bigText = '+' + entries.toLocaleString();
    var bigEl = flex.querySelector('[data-flex-big]');
    if (bigEl) bigEl.textContent = bigText;
    var reflEl = flex.querySelector('[data-flex-refl]');
    if (reflEl) reflEl.textContent = bigText;
    // Stub explains the rate instead of repeating the total: "4X / PER $1".
    var stubEl = flex.querySelector('[data-flex-stub]');
    if (stubEl) stubEl.textContent = _rate(mult) + 'X';
    flex.hidden = false;
    // Light the stage (idempotent; the module idles while hidden).
    if (window.SHFTDTakeoverScene && typeof window.SHFTDTakeoverScene.mount === 'function') {
      try { window.SHFTDTakeoverScene.mount(flex); } catch (_) {}
    }
  }

  function relabelOffers() {
    var gap = effectiveGap();
    var threshold = (function () {
      var el = document.querySelector('[data-ship-zone]');
      return el ? parseInt(el.getAttribute('data-threshold'), 10) || 0 : 0;
    })();
    var curSub = gap == null ? 0 : threshold - gap;
    shapeFlex(gap, curSub);
    var scored = [];
    document.querySelectorAll('[data-takeover-add]').forEach(function (btn) {
      if (btn.hasAttribute('data-flex-fixed')) return; // shaped by shapeFlex
      var offer = btn.closest('.drawer__takeover-offer');
      if (offer) offer.hidden = true;
      var unit = parseInt(btn.getAttribute('data-unit-price'), 10) || 0;
      if (!unit || gap == null || gap <= 0) return;
      var qty = Math.min(MAX_UNITS, Math.max(1, Math.ceil(gap / unit)));
      var total = qty * unit;
      var over = total - gap;
      if (over < 0 || over > MAX_OVER_CENTS) return; // undershoot (unit cap) or dumb overshoot
      btn.setAttribute('data-qty', qty);
      // Button says the action + the price once; the quantity lives on the
      // product name ("4x SHFTD Keychain") so the button stays clean.
      var priceLabel = total % 100 === 0 ? '$' + (total / 100) : _fmt(total);
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>ADD \u00b7 ' + priceLabel;
      btn.setAttribute('aria-label', 'Add ' + qty + ' for ' + _fmt(total));
      btn.disabled = false;
      var qtyBadge = offer ? offer.querySelector('[data-offer-qty]') : null;
      if (qtyBadge) {
        if (qty > 1) { qtyBadge.textContent = String(qty); qtyBadge.hidden = false; }
        else { qtyBadge.hidden = true; }
      }
      // Entries the add is worth: the units land at the NEW subtotal's tier
      // rate (product base rate + every multiplier rung the new subtotal
      // clears), plus flat bonus rungs crossed on the way. Understates when
      // a newly cleared rung also lifts the rest of the cart.
      var unitMult = parseFloat(btn.getAttribute('data-unit-mult')) || 1;
      var newSub = curSub + total;
      var offerRate = unitMult + tierAddsAt(newSub);
      var entries = qty * Math.round(offerRate * effectiveDollars(unit)) +
        flatBonusesBetween(curSub, newSub);
      paintOffer(offer, entries, offerRate);
      scored.push({
        offer: offer,
        // Band A (over <= $5): bigger push wins. Band B: closest wins.
        rank: over <= SWEET_OVER_CENTS ? (SWEET_OVER_CENTS - over) : (10000 + over),
      });
    });
    scored.sort(function (a, b) { return a.rank - b.rank; });
    scored.slice(0, MAX_OFFERS).forEach(function (s, i) {
      if (s.offer) { s.offer.hidden = false; s.offer.style.order = i; }
    });
  }

  function applyPendingRender() {
    if (!pendingSections) return false;
    var drawer = document.querySelector('cart-drawer');
    var res = pendingSections;
    pendingSections = null;
    pendingAddedCents = 0;
    if (drawer && res.sections && typeof drawer.renderContents === 'function') {
      // Replaces #CartDrawer's children, including this takeover (the fresh
      // copy arrives hidden), so the caller must not animate afterwards.
      drawer.renderContents(res);
      return true;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    return false;
  }

  function hideIntercept(refocus) {
    var card = takeoverEl();
    if (!card || card.hidden) return;
    if (applyPendingRender()) {
      // Fresh markup is already in the closed state; just restore focus.
      if (refocus) {
        var b = document.getElementById('CartDrawer-Checkout');
        if (b) b.focus();
      }
      return;
    }
    card.classList.remove('is-open');
    setTimeout(function () {
      card.hidden = true;
      if (refocus) {
        var btn = document.getElementById('CartDrawer-Checkout');
        if (btn) btn.focus();
      }
    }, switchMs());
  }

  function proceedToCheckout() {
    var form = document.getElementById('CartDrawer-Form');
    var btn = document.getElementById('CartDrawer-Checkout');
    var card = takeoverEl();
    if (card) { card.classList.remove('is-open'); card.hidden = true; }
    pendingSections = null;
    pendingAddedCents = 0;
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(btn || undefined);
    } else if (btn) {
      btn.click(); // seen-flag is already set, so the intercept won't re-fire
    }
  }

  // The "continue to checkout" escape must ALWAYS be on screen and this panel
  // must never scroll (owner rule). Short phones (iPhone SE at 667px, folded
  // Androids) cannot hold full fidelity, so shed one step at a time until the
  // panel fits its own height: chatter first, then scene size, then the second
  // physical card, then the first. The scene is the highest-margin closer, so
  // it shrinks last and is never dropped.
  var MAX_FIT_STEP = 5;

  function offerCards() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.drawer__takeover-offer')
    );
  }

  // The divider only earns its space when there is something on BOTH sides:
  // no scene (product not created yet) or no cards left after fitting means
  // it would just float above or below nothing.
  function syncOrDivider() {
    var el = document.querySelector('[data-takeover-or]');
    if (!el) return;
    var flex = document.querySelector('[data-takeover-flex]');
    var sceneShown = !!flex && !flex.hidden;
    var cardsShown = offerCards().some(function (o) { return !o.hidden; });
    el.hidden = !(sceneShown && cardsShown);
  }

  function fitTakeover() {
    var card = takeoverEl();
    if (!card || card.hidden) return;
    var pitch = card.querySelector('[data-takeover-pitch]');
    if (!pitch || pitch.hidden) return;
    // Start from full fidelity every time so a cart change (or a rotate back
    // to a tall viewport) restores what an earlier pass shed.
    card.removeAttribute('data-fit');
    offerCards().forEach(function (o) {
      if (o.getAttribute('data-fit-shed') === '1') {
        o.removeAttribute('data-fit-shed');
        o.hidden = false;
      }
    });
    syncOrDivider();
    for (var step = 1; step <= MAX_FIT_STEP; step++) {
      if (pitch.scrollHeight <= pitch.clientHeight) return;
      card.setAttribute('data-fit', String(step));
      if (step === 4 || step === 5) {
        // Drop physical cards bottom-up. Marked so the next pass can restore
        // them instead of mistaking them for ranked-out candidates.
        var shown = offerCards().filter(function (o) { return !o.hidden; });
        var victim = shown[shown.length - 1];
        if (victim) {
          victim.hidden = true;
          victim.setAttribute('data-fit-shed', '1');
          syncOrDivider();
        }
      }
    }
  }

  function showIntercept(gap) {
    var card = takeoverEl();
    if (!card) return;
    pendingSections = null;
    pendingAddedCents = 0;
    paintGap(gap);
    relabelOffers();
    markInterceptSeen();
    card.hidden = false;
    fitTakeover();
    setTimeout(function () {
      card.classList.add('is-open');
      card.focus();
    }, 20);
  }

  function showSuccess() {
    var pitch = document.querySelector('[data-takeover-pitch]');
    var win = document.querySelector('[data-takeover-success]');
    if (!pitch || !win) return;
    pitch.hidden = true;
    win.hidden = false;
  }

  // One-tap gap closer: add the computed qty, then either flip to the
  // success beat (threshold crossed) or update the gap and keep pitching.
  document.addEventListener('click', function (e) {
    var btn = e.target instanceof Element ? e.target.closest('[data-takeover-add]') : null;
    if (!btn || btn.disabled) return;
    var variantId = parseInt(btn.getAttribute('data-variant'), 10);
    var unit = parseInt(btn.getAttribute('data-unit-price'), 10) || 0;
    var qty = parseInt(btn.getAttribute('data-qty'), 10) || 1;
    if (!variantId) return;
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding\u2026';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: qty,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status) throw new Error(res.description || 'add failed');
        pendingSections = res;
        pendingAddedCents += qty * unit;
        var gap = effectiveGap();
        if (gap != null && gap <= 0) {
          showSuccess();
        } else {
          paintGap(gap);
          relabelOffers();
        }
      })
      .catch(function () {
        btn.textContent = 'Try again';
        setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1600);
      });
  });

  // Capture phase so this runs before the button's default form submission.
  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    var btn = e.target.closest('#CartDrawer-Checkout');
    if (!btn || btn.disabled) return;
    var gap = shipGapCents();
    if (!isNearMiss(gap) || interceptSeen()) return;
    if (!document.querySelector('[data-ship-intercept]')) return;
    e.preventDefault();
    showIntercept(gap);
  }, true);

  // Escape closes the card and proceeds nowhere. Handled on capture for both
  // key phases so the drawer's own Escape-to-close (keyup on <cart-drawer>)
  // doesn't also fire and close the whole drawer underneath the shopper.
  function onEscKey(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var card = document.querySelector('[data-ship-intercept]');
    if (!card || card.hidden) return;
    e.stopPropagation();
    if (e.type === 'keyup') hideIntercept(true);
  }
  document.addEventListener('keydown', onEscKey, true);
  document.addEventListener('keyup', onEscKey, true);

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('[data-ship-intercept-go]')) {
      proceedToCheckout();
      return;
    }
    if (e.target.closest('[data-ship-intercept-back]')) {
      hideIntercept(true);
      return;
    }
    if (e.target.closest('[data-ship-intercept-shop]')) {
      // Back to shopping: drop the takeover and close the drawer so the
      // shopper lands where they were browsing.
      hideIntercept(false);
      var drawerEl = document.querySelector('cart-drawer');
      if (drawerEl && typeof drawerEl.close === 'function') {
        setTimeout(function () { drawerEl.close(); }, switchMs());
      }
    }
  });

  // ---- Entries bonus bridge ----
  // The per-line pills show per-unit boosted entries; the claim total also
  // carries the flat tier bonuses, and nothing explains the gap. N = claim
  // total minus Σ(per-unit pill × qty), from attributes the entries pipeline
  // already maintains (data-entries on the claim span, data-count-value as
  // each capsule's settled tween target).
  var bridgeTimer = null;
  function scheduleBonusBridge() {
    clearTimeout(bridgeTimer);
    bridgeTimer = setTimeout(updateBonusBridge, 350);
  }

  function updateBonusBridge() {
    var el = document.querySelector('[data-entries-bonus]');
    if (!el) return;
    var claim = document.querySelector('.drawer__entries-count[data-entries]');
    var total = claim ? parseInt(claim.getAttribute('data-entries'), 10) : NaN;
    if (!Number.isFinite(total) || total <= 0) { el.hidden = true; return; }
    var sum = 0;
    var reliable = true;
    document.querySelectorAll('#CartDrawer .cart-item').forEach(function (row) {
      var cap = row.querySelector('.entries-capsule__count');
      if (!cap) return; // zero-price lines render no capsule and award nothing
      var perUnit = parseInt(cap.getAttribute('data-count-value'), 10);
      if (!Number.isFinite(perUnit)) {
        perUnit = parseInt((cap.textContent || '').replace(/[^\d]/g, ''), 10);
      }
      var input = row.querySelector('.quantity__input');
      var qty = input ? parseInt(input.value, 10) : NaN;
      if (!Number.isFinite(perUnit) || !Number.isFinite(qty)) {
        reliable = false;
        return;
      }
      sum += perUnit * qty;
    });
    var bonus = total - sum;
    if (reliable && bonus > 0) {
      el.textContent = ((window.shftdLocale&&window.shftdLocale.cd_tier_bonus)||"includes +__N__ bonus entries from your tier").replace("__N__", bonus.toLocaleString(((window.shftdLocale&&window.shftdLocale.number_locale)||"en-US")));
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  // ---- Wiring ----

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('#CartDrawer button[name="plus"], #CartDrawer button[name="minus"]')) {
      scheduleBonusBridge();
    }
  });
  document.addEventListener('change', function (e) {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest('#CartDrawer .quantity__input')) {
      scheduleBonusBridge();
    }
  });
  function bind() {
    scheduleBonusBridge();
    var drawer = document.getElementById('CartDrawer');
    if (!drawer || typeof MutationObserver !== 'function') return;
    // Full re-renders replace #CartDrawer's children (fresh Liquid markup);
    // re-sync the JS-derived bits on top of it.
    new MutationObserver(function () {
      scheduleBonusBridge();
    }).observe(drawer, { childList: true });
    // The entries pipeline updates data-entries / data-count-value in place
    // (no child-list change), so watch those attributes too. The bridge only
    // writes textContent + hidden on its own node, neither is in the filter,
    // so this can't self-trigger.
    new MutationObserver(scheduleBonusBridge).observe(drawer, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-entries', 'data-count-value'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
